"use client";
import { Button, Checkbox } from "@ama-pt/agora-design-system";
import Image from "@/components/atoms/image";
import { ReactNode, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Heading from "@/components/molecules/Heading";
import { useTranslations } from "next-intl";

export interface NewsletterProps {
  title: ReactNode;
  subtitle: string;
  emailLabel: string;
  btnLabel: string;
  placeholder: string;
  imageUrl: string;
  imageAlt: string;
  successMessageImageUrl: string;
  successMessageImageAlt: string;
  successMessage?: string;
  successMessageDescription?: string;
  errorMessage?: string;
  backButtonLabel?: string;
  consentInputLabel?: string;
}

export const Newsletter = ({
  btnLabel = "",
  title = "",
  subtitle,
  emailLabel,
  placeholder,
  imageAlt,
  imageUrl,
  successMessage,
  successMessageDescription,
  backButtonLabel,
  consentInputLabel,
  errorMessage,
  successMessageImageAlt,
  successMessageImageUrl,
}: NewsletterProps) => {
  const t = useTranslations("form");
  const [email, setEmail] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [state, setState] = useState<"open" | "error" | "done">("open");

  const { isMobile } = useMediaQuery();

  const validateEmailFormat = (emailValue: string) => {
    const emailRegex = new RegExp(
      "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
    );
    return emailRegex.test(emailValue);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (validateEmailFormat(value)) {
      if (!isValid) setIsValid(true);
    } else {
      if (isValid) setIsValid(false);
    }
  };

  const handleSubmit = async () => {
    if (!email) return;

    setSubmitting(true);

    const formData = new FormData();

    formData.append("CONTACT_EMAIL", email);
    formData.append("submitType", "optinCustomView");
    formData.append("formType", "QuickForm");
    formData.append("zx", "14aed1e686");
    formData.append("zcvers", "2.0");
    formData.append("mode", "OptinCreateView");
    formData.append("zctd", "13ccc5a75e98a541");
    formData.append("zc_trackCode", "ZCFORMVIEW");
    formData.append(
      "zc_formIx",
      "3zdd6bf5504ab30db4cfffa24d47f7b2e17faabe90d9034fcf8b36bb8bb99f9ac8"
    );

    try {
      await fetch(
        "https://ebup-zcmp.maillist-manage.eu/weboptin.zc",
        {
          method: "POST",
          mode: "no-cors",
          body: formData,
        }
      );

      setState("done");
    } catch (err) {
      console.error(err);
      setState("error");
    } finally {
      setSubmitting(false);
    }
  };


  const goBack = () => {
    setEmail("");
    setState("open");
    setIsChecked(false);
  };

  if (state === "done")
    return (
      <div className="flex w-full overflow-hidden">
        <div className="md:container mx-auto">
          <div className="bg-blue-100 flex w-full px-[32px] py-[32px] md:py-[110px] md:px-[32px] xl:py-[90px] xl:px-[64px]">
            <div className="flex-1">
              <div className="flex md:flex-row flex-col-reverse gap-[32px] my-auto">
                <div className="flex flex-col flex-1 my-auto">
                  <div className="flex flex-col gap-[32px] md:gap-[16px]">
                    <div className="text-xl-regular leading-[28px] md:text-2xl-regular xl:text-3xl-regular text-neutral-900 bg-no-repeat bg-[length:100%_50%] bg-top">
                      <Heading notitleEffect title={successMessage} />
                    </div>
                    <p className="py-16 text-neutral-900 text-m-regular"> {successMessageDescription} </p>
                    <div className="w-full flex md:hidden">
                      <Button
                        hasIcon="true"
                        leadingIcon="agora-line-arrow-left-circle"
                        onClick={goBack}
                        disabled={submitting}
                        fullWidth
                      >
                        {backButtonLabel}
                      </Button>
                    </div>
                    <div className="w-auto hidden md:flex">
                      <Button
                        hasIcon="true"
                        leadingIcon="agora-line-arrow-left-circle"
                        onClick={goBack}
                        disabled={submitting}
                      >
                        {backButtonLabel}
                      </Button>
                    </div>
                  </div>
                </div>
                <div
                  className="w-[296px] xl:w-[376px]  mx-auto md:mx-0 md:w-[236px] aspect-[1.2] relative flex "
                  aria-hidden="true"
                >
                  <Image
                    fill
                    sizes="400px"
                    src={successMessageImageUrl}
                    alt={successMessageImageAlt}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="flex w-full overflow-hidden">
      <div className="md:container mx-auto">
        <div className="bg-blue-100 flex w-full p-[32px] xl:p-[64px]">
          <div className="flex-1">
            <div className="flex md:flex-row flex-col-reverse gap-[32px]">
              <div className="flex flex-col mb-[32px] xl:mb-[64px] flex-1">
                <div className="flex flex-col gap-[8px] md:gap-[16px]">
                  <div className="text-xl-regular leading-[28px] md:text-2xl-regular xl:text-3xl-regular text-neutral-900 bg-no-repeat bg-[length:100%_50%] bg-top">
                    <Heading
                      notitleEffect
                      title={title}
                      description={subtitle}
                    />
                  </div>
                </div>
              </div>
              <div
                className="w-[296px] mx-auto md:mx-0 md:w-[236px] aspect-[1.2] relative flex xl:hidden"
                aria-hidden="true"
              >
                <Image fill sizes="400px" src={imageUrl} alt={imageAlt} />
              </div>
            </div>

            <div className="flex flex-col newsletter">
             

              {state === "error" ? (
                <span className="text-m-bold text-danger-500 mb-[16px] leading-[28px]">
                  {errorMessage}
                </span>
              ) : (
                <label htmlFor="newsletter-email" className="text-m-bold text-neutral-900 mb-[16px] leading-[28px]">
                  <span className="text-danger-500 text-[24px]">* </span>
                  {emailLabel} &nbsp;
                  <span className="text-s-regular text-danger-700">({t("requiredFieldNote")})</span>
                </label>
              )}
              <div className="relative">
                <div className="w-full relative">
                  <input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    aria-required="true"
                    placeholder={placeholder}
                    value={email}
                    onChange={handleEmailChange}
                    required
                    pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                    className="h-[76px] w-full pl-[16px] pr-[80px] py-[24px] rounded-[8px] b-0"
                  />
                </div>
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  className="mt-[10px]"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {consentInputLabel}
                      </>
                    ) as any
                  }
                />
                <div className="mt-[16px] w-full md:absolute md:right-[8px] md:top-[8px] md:z-10 md:mt-0 md:w-auto">
                  {!isMobile ? (
                    <Button
                      hasIcon="true"
                      name="newsletter-submit"
                      aria-label={btnLabel}
                      leadingIcon="agora-line-mail"
                      onClick={handleSubmit}
                      disabled={submitting || !isValid || !isChecked || !email}
                      className={`${isMobile ? "leading-[0px]" : ""}`}
                    >
                      {submitting ? "Enviando..." : btnLabel}
                    </Button>
                  ) : (
                    <Button
                      hasIcon="true"
                      aria-label="Enviar newsletter"
                      name="newsletter-submit"
                      leadingIcon="agora-line-mail"
                      onClick={handleSubmit}
                      disabled={submitting || !isValid || !isChecked || !email}
                      className={`${isMobile ? "leading-[0px]" : ""}`}
                      fullWidth
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="w-[376px] aspect-[1.2] relative hidden xl:flex"
            aria-hidden="true"
          >
            {imageUrl && (
              <Image fill sizes="400px" src={imageUrl} alt={""} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
